import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, Lock, User, Building2, Phone, Briefcase, Eye, EyeOff } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { getApiEndpoint } from "@/lib/api";
import { formatPersonName, formatPhoneNumber } from "@/utils/mask";
import { useLanguage } from "@/contexts/LanguageContext";

const mascotSrc = "/login-assets/tec-it-mascot.jpeg";
const videoSrc = "/login-assets/login-background.mp4";

const Register = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    company_domain: "",
    sector: "",
    function: ""
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    if (!formData.name.trim()) {
      toast({ title: t.register.error, description: t.register.nameRequired, variant: "destructive" });
      setIsLoading(false);
      return;
    }
    if (!formData.email.trim()) {
      toast({ title: t.register.error, description: t.register.emailRequired, variant: "destructive" });
      setIsLoading(false);
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast({ title: t.register.error, description: t.register.invalidEmail, variant: "destructive" });
      setIsLoading(false);
      return;
    }
    if (!formData.phone.trim()) {
      toast({ title: t.register.error, description: t.register.phoneRequired, variant: "destructive" });
      setIsLoading(false);
      return;
    }
    if (!formData.company_domain.trim()) {
      toast({ title: t.register.error, description: t.register.domainRequired, variant: "destructive" });
      setIsLoading(false);
      return;
    }
    if (!formData.sector.trim()) {
      toast({ title: t.register.error, description: t.register.sectorRequired, variant: "destructive" });
      setIsLoading(false);
      return;
    }
    if (!formData.function.trim()) {
      toast({ title: t.register.error, description: t.register.functionRequired, variant: "destructive" });
      setIsLoading(false);
      return;
    }
    if (!formData.password || formData.password.length < 6) {
      toast({ title: t.register.error, description: t.register.passwordMin, variant: "destructive" });
      setIsLoading(false);
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      toast({ title: t.register.error, description: t.register.passwordsDontMatch, variant: "destructive" });
      setIsLoading(false);
      return;
    }
    try {
      const response = await fetch(getApiEndpoint("/api/auth/register"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formatPersonName(formData.name),
          email: formData.email.trim().toLowerCase(),
          password: formData.password,
          phone: formData.phone.trim(),
          company_domain: formData.company_domain.trim(),
          sector: formData.sector.trim(),
          function: formData.function.trim()
        }),
      });
      const data = await response.json();
      if (response.ok) {
        toast({ title: t.register.registerSuccess, description: t.register.registerSuccessDesc });
        setTimeout(() => navigate("/login"), 2000);
      } else {
        toast({ title: t.register.registerError, description: data.error || t.auth.connectionError, variant: "destructive" });
      }
    } catch {
      toast({ title: t.register.registerError, description: t.auth.connectionError, variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  const inputClass = "pl-11 h-12 bg-input dark:bg-gray-800 border-border/50 dark:border-gray-600 focus:border-violet-500 focus:ring-violet-500/20 transition-all duration-300 dark:text-white placeholder:text-muted-foreground dark:placeholder:text-gray-500";
  const labelClass = "text-sm text-muted-foreground dark:text-gray-400";
  const hintClass = "text-xs text-muted-foreground dark:text-gray-500 mt-1";

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden bg-[#0f172a]">
      <video autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover">
        <source src={videoSrc} type="video/mp4" />
      </video>
      <div className="absolute inset-0 bg-background/70 backdrop-blur-sm dark:bg-[#0f172a]/80" />
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-violet-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
      </div>

      <div className="absolute bottom-6 right-6 opacity-20 hover:opacity-40 transition-opacity duration-500 z-0">
        <div className="w-20 h-20 rounded-full overflow-hidden">
          <img src={mascotSrc} alt="" className="w-full h-full object-cover object-top scale-110 grayscale" />
        </div>
      </div>

      <div className="relative w-full max-w-md max-h-[90vh] overflow-y-auto z-10 animate-fade-in">
        <div className="glass-card rounded-2xl p-8 glow-primary dark:bg-gray-900/90 dark:border-gray-700/50">
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="w-28 h-28 rounded-full overflow-hidden border-4 border-violet-500/30 shadow-2xl dark:border-violet-400/30">
                <img src={mascotSrc} alt="TEC-IT" className="w-full h-full object-cover object-top scale-110" />
              </div>
              <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center border-2 border-gray-900 dark:border-gray-900 shadow-lg">
                <span className="text-white text-xs font-bold">✓</span>
              </div>
            </div>
          </div>

          <div className="text-center mb-6">
            <p className="text-sm text-muted-foreground uppercase tracking-widest mb-2 dark:text-gray-400">
              {t.auth.centralDeServicos}
            </p>
            <h1 className="text-2xl font-semibold text-foreground dark:text-white">
              TEC-IT <span className="text-violet-500 dark:text-violet-400">ITSM</span>
            </h1>
          </div>

          <h2 className="text-base font-medium text-foreground dark:text-white mb-1">{t.register.title}</h2>
          <p className="text-sm text-muted-foreground dark:text-gray-400 mb-5">{t.register.desc}</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className={labelClass}>{t.register.name} *</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground dark:text-gray-500" />
                <Input
                  id="name"
                  type="text"
                  placeholder={t.register.namePlaceholder}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  onBlur={() => setFormData((prev) => ({ ...prev, name: formatPersonName(prev.name) }))}
                  className={inputClass}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className={labelClass}>{t.auth.email} *</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground dark:text-gray-500" />
                <Input
                  id="email"
                  type="email"
                  placeholder={t.auth.emailPlaceholder}
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={inputClass}
                  required
                />
              </div>
              <p className={hintClass}>{t.register.emailHint}</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className={labelClass}>{t.register.phone} *</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground dark:text-gray-500" />
                <Input
                  id="phone"
                  type="tel"
                  placeholder={t.register.phonePlaceholder}
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: formatPhoneNumber(e.target.value) })}
                  className={inputClass}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="company_domain" className={labelClass}>{t.register.companyDomain} *</Label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground dark:text-gray-500" />
                <Input
                  id="company_domain"
                  type="text"
                  placeholder={t.register.companyDomainPlaceholder}
                  value={formData.company_domain}
                  onChange={(e) => setFormData({ ...formData, company_domain: e.target.value })}
                  className={inputClass}
                  required
                />
              </div>
              <p className={hintClass}>{t.register.companyDomainHint}</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="sector" className={labelClass}>{t.register.sector} *</Label>
              <div className="relative">
                <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground dark:text-gray-500" />
                <Input
                  id="sector"
                  type="text"
                  placeholder={t.register.sectorPlaceholder}
                  value={formData.sector}
                  onChange={(e) => setFormData({ ...formData, sector: e.target.value })}
                  className={inputClass}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="function" className={labelClass}>{t.register.function} *</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground dark:text-gray-500" />
                <Input
                  id="function"
                  type="text"
                  placeholder={t.register.functionPlaceholder}
                  value={formData.function}
                  onChange={(e) => setFormData({ ...formData, function: e.target.value })}
                  className={inputClass}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className={labelClass}>{t.auth.password} *</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground dark:text-gray-500" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className={`${inputClass} pr-11`}
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground dark:text-gray-400 dark:hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className={hintClass}>{t.register.passwordHint}</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className={labelClass}>{t.register.confirmPassword} *</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground dark:text-gray-500" />
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className={`${inputClass} pr-11`}
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground dark:text-gray-400 dark:hover:text-white transition-colors"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-12 bg-violet-600 hover:bg-violet-700 dark:bg-violet-600 dark:hover:bg-violet-700 text-white font-medium transition-all duration-300 hover:shadow-lg hover:shadow-violet-500/25"
              disabled={isLoading}
            >
              {isLoading ? t.register.creatingAccount : t.register.createButton}
            </Button>

            <p className="text-center text-sm text-muted-foreground dark:text-gray-400">
              {t.auth.hasAccount}{" "}
              <Link to="/login" className="text-violet-500 hover:text-violet-400 dark:text-violet-400 dark:hover:text-violet-300 font-medium transition-colors">
                {t.auth.signInLink}
              </Link>
            </p>
          </form>

          <p className="text-center text-muted-foreground dark:text-gray-500 text-xs mt-8">
            {t.auth.tagline}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
